import classNames from 'classnames';
import * as React from 'react';
import { connect } from 'react-redux';

import { Course } from '@app/interfaces/store/content/program-rule';
import { Store } from '@app/interfaces/store/store';
import { mapDispatchToProps } from '@app/store/map-dispatch-to-props';

import { PURCHASE_TAB_STRINGS, TabIdType } from './purchase-form.constants';
import { PurchaseOptions } from './purchase-form.types';
import {
	formUtil,
	getMinMaxPriceFromOptions,
	getPriceRange,
	getPurchaseOptions,
	localStorageUtil,
	tabUtil,
} from './purchase-form.util';
import { PurchaseTabExam } from './purchase-tab-exam';
import { PurchaseTabMaterial } from './purchase-tab-material';
import { PropsBaseButton } from '../button';

type PropPurchaseForm = {
	actions: any;
	className?: string;
	course: Course;
	groupName?: string;
	singleTab?: TabIdType;
	store: Store;
	toggleButtonElement?: React.FC<PropsBaseButton>;
	toggleClassNames?: string;
	toggleText?: string;
};

const { MATERIAL_TAB, EXAM_TAB } = PURCHASE_TAB_STRINGS;

/**
 * Purchase form is the main purchasing experience for TI products. The form is
 * split into two tabs: Materials & Exams. If singleTab is defined, then the
 * form will only render the specified singleTab.
 *
 * Both tabs present users exam windows options, before selecting the respective
 * products.
 *
 * If a toggleButtonElement is defined, then the form is visible only after the
 * user clicks the toggle button element.
 *
 * @param {Course} props.course Required. Course object.
 * @param {string} props.className Optional. Purchase form class name.
 * @param {TabIdType=} props.singleTab Optional. Will only render specific tab.
 * @param {string=} props.groupName Optional. Will add string to component key.
 * @param {React.FC<PropsBaseButton>} props.toggleButtonElement Optional. Form will be visible once a button is pressed.
 * @param {string} props.toggleText Optional. Overrides any default toggle button label.
 * @param {string} props.toggleClassName Optional. Classname for toggle button.
 * @return {React.ReactElement} Purchase Form component.
 */
const PurchaseFormRaw: React.FC<PropPurchaseForm> = ({
	actions,
	className = '',
	course,
	groupName = '',
	singleTab,
	store,
	toggleButtonElement,
	toggleClassNames = '',
	toggleText,
}: PropPurchaseForm): React.ReactElement => {
	const {
		// abbreviation,
		code,
		examWindows,
		id,
		note,
		materialsNote,
	} = course;

	const { programRule, marketing } = store.content;

	// State for form is visible or behind a button toggle.
	const [isVisible, setIsVisible] = React.useState(
		toggleButtonElement === undefined,
	);

	// State for current active tab.
	const [activeTab, setActiveTab] = React.useState<TabIdType>(
		singleTab || MATERIAL_TAB.id,
	);

	// State for select options for both tabs.
	const [purchaseOptions, setPurchaseOptions] = React.useState<PurchaseOptions>(
		{ examOptions: [], materialOptions: [] },
	);

	const { materialOptions, examOptions } = purchaseOptions;

	// State for form dimensions.
	// Form dimensions change to support any card overlay.
	const [{ materialHeight, examHeight, height }, setFormDimensions] =
		React.useState<{
			materialHeight?: string;
			examHeight?: string;
			height?: string;
		}>({
			materialHeight: 'auto',
			examHeight: 'auto',
			height: 'auto',
		});

	// Refs for tab buttons.
	const materialRef = React.useRef<HTMLButtonElement>(null);
	const examRef = React.useRef<HTMLButtonElement>(null);

	// Hide exam windows for WCCA.
	// const hasNoExamWindows =
	// abbreviation !== undefined && /^WCCA/.test(abbreviation);

	// Do not hide exam windows for WCCA.
	const hasNoExamWindows = false;

	/**
	 * Print price range based on tab id.
	 *
	 * @param {TabIdType} tabId Tab ID.
	 * @return {JSX.Element} Price Range element.
	 */
	const printPriceRange = React.useCallback(
		(tabId: TabIdType): JSX.Element | null => {
			let maxPrice;
			let minPrice;

			if (tabId === MATERIAL_TAB.id) {
				const matOptions = materialOptions?.flatMap((mat) => mat.options) || [];
				[minPrice, maxPrice] = getMinMaxPriceFromOptions(matOptions);
			}

			if (tabId === EXAM_TAB.id) {
				const exOptions = examOptions?.flatMap(({ options }) => [
					...options.INTERNET,
					...options.VIRTUAL,
				]) || [];
				[minPrice, maxPrice] = getMinMaxPriceFromOptions(exOptions);
			}

			const priceRange = getPriceRange(minPrice, maxPrice);

			return priceRange ? (
				<>
					<br />
					{priceRange}
				</>
			) : null;
		},
		[materialOptions, examOptions],
	);

	/**
	 * Callback to set active tab and set active tab height.
	 *
	 * @param {TabIdType} tab Tab ID.
	 */
	const changeTab = React.useCallback(
		(tab: TabIdType) => {
			setActiveTab(tab);
			setFormDimensions({
				height: tab === MATERIAL_TAB.id ? materialHeight : examHeight,
			});

			if (tab === MATERIAL_TAB.id && materialRef?.current) {
				materialRef.current.focus();
			}
			if (tab === EXAM_TAB.id && examRef?.current) {
				examRef.current.focus();
			}
		},
		[examHeight, materialHeight, setActiveTab, setFormDimensions],
	);

	/**
	 * Callback to set container height for tab type.
	 *
	 * @param {string} value Value for CSS height attribute.
	 * @param {TabIdType} TabId Tab ID.
	 */
	const setContainerHeight = React.useCallback(
		(value: string, tabId: TabIdType) => {
			if (value && tabId && [MATERIAL_TAB.id, EXAM_TAB.id].includes(tabId)) {
				if (tabId === MATERIAL_TAB.id) {
					setFormDimensions({
						materialHeight: value,
						height: value,
					});
				} else {
					setFormDimensions({ examHeight: value, height: value });
				}
			}
		},
		[setFormDimensions],
	);

	/**
	 * Handles arrow key navigation for tabs.
	 *
	 * @param  {React.KeyboardEvent<HTMLButtonElement>} event Keyboard event.
	 * @listens document#keydown
	 */
	const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
		if (activeTab === MATERIAL_TAB.id && event.key === 'ArrowRight') {
			changeTab(EXAM_TAB.id);
		} else if (activeTab === EXAM_TAB.id && event.key === 'ArrowLeft') {
			changeTab(MATERIAL_TAB.id);
		}
	};

	/**
	 * Checks if option is defined and not empty.
	 *
	 * @param options Options to validate.
	 * @returns {boolean}
	 */
	function isOptionValid<T>(options?: T[]): boolean {
		return options !== undefined && options.length > 0;
	}

	// Use effect to set up local storage state.
	React.useEffect(() => {
		localStorageUtil.loadTab(changeTab, code);
	}, [changeTab, code]);

	// Use effect to determine which purchase options to render.
	React.useEffect(() => {
		const options = getPurchaseOptions(examWindows, programRule, marketing);
		const { examOptions: examOpts, materialOptions: matOptions } = options;

		// Capture if options are exclusively valid for a single tab.
		const onlyExamOptions =
			isOptionValid(examOpts) && !isOptionValid(matOptions);
		const onlyMaterialOptions =
			isOptionValid(matOptions) && !isOptionValid(examOpts);

		if (singleTab === EXAM_TAB.id || (!singleTab && onlyExamOptions)) {
			setActiveTab(EXAM_TAB.id);
			return setPurchaseOptions({ examOptions: examOpts });
		}

		if (singleTab === MATERIAL_TAB.id || (!singleTab && onlyMaterialOptions)) {
			setActiveTab(MATERIAL_TAB.id);
			return setPurchaseOptions({ materialOptions: matOptions });
		}

		return setPurchaseOptions(options);
	}, [
		examWindows,
		marketing,
		programRule,
		setPurchaseOptions,
		setActiveTab,
		singleTab,
	]);

	// Show material tab is material options exist.
	const showMaterialTab = isOptionValid(materialOptions);

	// Show exam tab if exam options exist.
	const showExamTab = isOptionValid(examOptions);

	// Show tab buttons if both purchase options exist.
	const showTabButtons = showMaterialTab && showExamTab;

	// Show as single tab if purchase options are limited to one.
	const isSingleTab =
		(showMaterialTab && !showExamTab) || (!showMaterialTab && showExamTab);

	// Has no valid options.
	const hasNoOptions = !showExamTab && !showMaterialTab;

	// If no materials or exam options exist for a singleTab purchase option, then
	// do not display purchase form.
	if (hasNoOptions) {
		return <></>;
	}

	// If purchase form is not visible due to a toggle button element, then
	// render toggle button element first.
	if (!isVisible && toggleButtonElement !== undefined) {
		const ButtonElement = toggleButtonElement;
		const label = (() => {
			if (toggleText) {
				return toggleText;
			}

			// Default texts based on singleTab.
			switch (singleTab) {
				case EXAM_TAB.id:
					return 'Purchase Exam';
				case MATERIAL_TAB.id:
					return 'Purchase Materials';
				default:
				case undefined:
					return 'Purchase Materials & Exam';
			}
		})();

		return (
			<ButtonElement
				ariaLabel={label}
				fullWidthMobile
				onClick={() => setIsVisible(true)}
				className={toggleClassNames}
			>
				{label}
			</ButtonElement>
		);
	}

	return (
		<div
			className={classNames('purchase-tabs__container', className)}
			style={{ height }}
		>
			{showTabButtons && (
				<div
					className="purchase-tabs__tab-list"
					role="tablist"
					aria-label={`Purchase Options for ${course.abbreviation}`}
				>
					{[MATERIAL_TAB.id, EXAM_TAB.id].map((tabId) => (
						<button
							key={tabId}
							type="button"
							id={tabUtil.setId(tabId, id)}
							className={tabUtil.setClassName(tabId, activeTab)}
							role="tab"
							aria-controls={tabUtil.setPanelId(tabId, id)}
							tabIndex={tabUtil.setTabIndex(tabId, activeTab)}
							onClick={() => changeTab(tabId)}
							onKeyDown={handleKeyDown}
							ref={tabId === MATERIAL_TAB.id ? materialRef : examRef}
						>
							<strong>{PURCHASE_TAB_STRINGS[tabId].title}</strong>
							{printPriceRange(tabId)}
						</button>
					))}
				</div>
			)}
			{showMaterialTab && (
				<PurchaseTabMaterial
					actions={actions}
					courseCode={code}
					description={MATERIAL_TAB.description}
					examWindowOptions={materialOptions}
					form={formUtil.createFormName(
						MATERIAL_TAB.formName,
						id.toString(),
						groupName,
					)}
					hasNoExamWindows={hasNoExamWindows}
					hidden={activeTab !== MATERIAL_TAB.id}
					id={id}
					isSingleTab={isSingleTab}
					materialsNote={materialsNote}
					onSelect={() => changeTab(EXAM_TAB.id)}
					setContainerHeight={setContainerHeight}
					store={store}
				/>
			)}
			{showExamTab && (
				<PurchaseTabExam
					actions={actions}
					courseCode={code}
					description={EXAM_TAB.description}
					examWindowOptions={examOptions}
					form={formUtil.createFormName(
						EXAM_TAB.formName,
						id.toString(),
						groupName,
					)}
					hasNoExamWindows={hasNoExamWindows}
					hidden={activeTab !== EXAM_TAB.id}
					id={id}
					isSingleTab={isSingleTab}
					note={note}
					onSelect={() => changeTab(MATERIAL_TAB.id)}
					setContainerHeight={setContainerHeight}
					store={store}
				/>
			)}
		</div>
	);
};

export const PurchaseForm = connect(
	(store: Store) => ({ store }),
	mapDispatchToProps,
)(PurchaseFormRaw);
